import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardActionArea, CardContent, CircularProgress, Grid, Typography } from '@mui/material';
import { CameraAlt as CameraIcon, Celebration as CelebrationIcon, Lock as LockIcon, Logout as LogoutIcon, Star as StarIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ParentPortalLayout from '../components/ParentPortalLayout';
import LanguageSelector from '../components/LanguageSelector';
import { useParentAuth } from '../hooks/useParentAuth';
import { parentPortalAPI } from '../utils/api';
import type { SubscriptionStatus } from '../types/parent';
import { parentPortalTheme } from '../theme/parentPortalTheme';

const ParentDashboard: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'parentPortal' });
  const { logout, refreshProfile, isSubscribed } = useParentAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const subscribed = isSubscribed || subscription?.status === 'active';

  useEffect(() => {
    const loadSubscription = async () => {
      setLoadingSubscription(true);
      try {
        const response = await parentPortalAPI.getSubscription();
        setSubscription(response.subscription || response.data || null);
      } catch {
        setSubscription(null);
      } finally {
        setLoadingSubscription(false);
      }
    };
    loadSubscription();
  }, []);

  const cancelSubscription = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!window.confirm(t('cards.subscribe.cancelConfirm'))) return;
    setError(null);
    try {
      await parentPortalAPI.cancelSubscription();
      await refreshProfile();
      const response = await parentPortalAPI.getSubscription();
      setSubscription(response.subscription || response.data || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('cancelFailed'));
    }
  };

  const cards = [
    {
      title: t('cards.snapshots.title'),
      subtitle: t('cards.snapshots.subtitle'),
      path: '/parent/snapshots',
      icon: <CameraIcon sx={{ fontSize: 48 }} />,
    },
    {
      title: subscribed ? t('cards.subscribe.manageTitle') : t('cards.subscribe.title'),
      subtitle: subscribed
        ? `${subscription?.plan_name || t('cards.subscribe.activePlan')}${subscription?.current_period_end ? ` • ${t('cards.subscribe.renews', { date: new Date(subscription.current_period_end).toLocaleDateString() })}` : ''}`
        : t('cards.subscribe.subtitle'),
      path: '/parent/subscribe',
      icon: <StarIcon sx={{ fontSize: 48 }} />,
      manage: subscribed,
    },
    {
      title: t('cards.settings.title'),
      subtitle: t('cards.settings.subtitle'),
      path: '/parent/settings',
      icon: <LockIcon sx={{ fontSize: 48 }} />,
    },
    {
      title: t('cards.events.title'),
      subtitle: t('cards.events.subtitle'),
      path: '/parent/events',
      icon: <CelebrationIcon sx={{ fontSize: 48 }} />,
    },
  ];

  return (
    <ParentPortalLayout>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 2 }}>
        <LanguageSelector />
        <Button
          startIcon={<LogoutIcon />}
          onClick={() => { logout(); navigate('/parent/login'); }}
          sx={{ borderRadius: 999, color: parentPortalTheme.text.primary }}
        >
          {t('logout')}
        </Button>
      </Box>
      <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 5 } }}>
        <Typography variant="h3" sx={{ fontWeight: 900, color: parentPortalTheme.text.primary, letterSpacing: 0, textTransform: 'uppercase' }}>
          {t('brand')}
        </Typography>
        <Typography sx={{ color: parentPortalTheme.text.secondary, fontWeight: 800, textTransform: 'uppercase', mt: 1 }}>
          {t('tagline')}
        </Typography>
        <Typography aria-hidden sx={{ color: parentPortalTheme.text.primary, mt: 1.5, letterSpacing: 4 }}>
          - - - • • • - - -
        </Typography>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Grid container spacing={3} sx={{ maxWidth: 860, mx: 'auto' }}>
        {cards.map((card, index) => (
          <Grid key={card.path} size={{ xs: 12, sm: 6 }}>
            <Card sx={{ borderRadius: 6, bgcolor: parentPortalTheme.card.colors[index], border: `3px solid ${parentPortalTheme.card.border}`, minHeight: 250, boxShadow: parentPortalTheme.card.shadow }}>
              <CardActionArea onClick={() => navigate(card.path)} sx={{ height: '100%' }}>
                <CardContent sx={{ minHeight: 250, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', p: 3 }}>
                  <Box sx={{ color: parentPortalTheme.text.primary, mb: 2 }}>{card.icon}</Box>
                  <Typography sx={{ fontWeight: 900, textTransform: 'uppercase', color: parentPortalTheme.text.primary, fontSize: '1.25rem' }}>
                    {card.title}
                  </Typography>
                  <Typography sx={{ color: parentPortalTheme.text.secondary, mt: 1, flex: 1 }}>{loadingSubscription && card.manage ? <CircularProgress size={16} /> : card.subtitle}</Typography>
                  {card.manage && (
                    <Button onClick={cancelSubscription} sx={{ borderRadius: 999, color: parentPortalTheme.button.cancel, mb: 1 }}>
                      {t('cards.subscribe.cancel')}
                    </Button>
                  )}
                  <Typography sx={{ fontWeight: 900, color: parentPortalTheme.text.primary, mt: 'auto' }}>{t('open')}</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </ParentPortalLayout>
  );
};

export default ParentDashboard;
